import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { Menu, anchorFrom, type Anchor } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { errorMessage } from '../../lib/api';
import { dmsApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { dmName, dmParticipants, isUnread, mentionCount } from '../../store/selectors';
import { toast, useUi } from '../../store/ui';
import { GroupDmSettings } from '../dms/GroupDmSettings';
import { ServerIcon } from './ServerIcon';

/** Liste der Direktnachrichten und Gruppen. */
export function DmSidebar() {
  const ids = useAppStore((s) => s.dmIds);
  return (
    <div className="sidebar-inner" data-testid="dm-sidebar">
      <header className="sidebar-header">
        <NavLink to={paths.home()} end className="dm-home-link" data-testid="dm-home-link">
          Lagerfeuer-Start
        </NavLink>
      </header>
      <div className="channel-list scroll">
        <div className="category-row">
          <span className="category-toggle static">Direktnachrichten</span>
          <Tooltip label="Neue Direktnachricht">
            <button className="icon-btn icon-btn-xs" onClick={() => overlays.open({ type: 'newDm' })} data-testid="new-dm">
              <Plus size={16} />
            </button>
          </Tooltip>
        </div>
        <ul className="dm-list" role="list" data-testid="dm-list">
          {ids.map((id) => (
            <DmRow key={id} channelId={id} />
          ))}
        </ul>
        {ids.length === 0 && <p className="empty small">Noch keine Unterhaltungen. Starte eine mit „+".</p>}
      </div>
    </div>
  );
}

function DmRow({ channelId }: { channelId: string }) {
  const ch = useAppStore((s) => s.channels[channelId]);
  const me = useAppStore((s) => s.me);
  const meId = me?.id;
  const unread = useAppStore((s) => isUnread(s, channelId));
  const mentions = useAppStore((s) => mentionCount(s, channelId));
  const { channelId: active } = useParams();
  const navigate = useNavigate();
  // Kontextmenü (Rechtsklick/Kontextmenü-Taste) und Gruppenverwaltung (Threads/Foren/DMs).
  const [menu, setMenu] = useState<Anchor | null>(null);
  const [manage, setManage] = useState(false);
  if (!ch) return null;
  const others = (ch.recipients ?? []).filter((u) => u.id !== meId);
  const name = dmName(ch, meId);
  const close = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    dmsApi
      .close(channelId)
      .then(() => {
        useAppStore.getState().update((d) => {
          d.dmIds = d.dmIds.filter((x) => x !== channelId);
        });
        if (active === channelId) void navigate(paths.home());
      })
      .catch((err) => toast.error('Fehler', errorMessage(err)));
  };
  const group = ch.type === 'GROUP_DM';
  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenu(e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : anchorFrom(e.currentTarget));
  };
  return (
    <li onContextMenu={onContextMenu} className={`dm-row ${active === channelId ? 'active' : ''} ${unread ? 'unread' : ''}`} data-testid="dm-item" data-channel-id={channelId} data-unread={unread ? 'true' : undefined}>
      <NavLink to={paths.dm(channelId)} className="dm-link" onClick={() => useUi.getState().setNavOpen(false)} data-testid="dm-link">
        {ch.type === 'GROUP_DM' ? <ServerIcon server={{ name, iconUrl: ch.iconUrl }} size={32} /> : <Avatar user={others[0]} size={32} showStatus />}
        <span className="dm-text">
          <span className="truncate dm-name">{name}</span>
          {ch.type === 'GROUP_DM' && <span className="small faint">{dmParticipants(ch, me).length} Mitglieder</span>}
        </span>
      </NavLink>
      {mentions > 0 && <span className="badge">{mentions}</span>}
      <button className="icon-btn icon-btn-xs dm-close" onClick={close} aria-label={ch.type === 'GROUP_DM' ? 'Gruppe verlassen' : 'Unterhaltung schließen'} data-testid="dm-close">
        <X size={14} />
      </button>
      {menu && (
        <Menu
          anchor={menu}
          onClose={() => setMenu(null)}
          testId="dm-menu"
          items={[
            { id: 'profile', label: 'Profil anzeigen', hidden: group || !others[0], onSelect: () => others[0] && overlays.profile(others[0].id, menu) },
            { id: 'settings', label: 'Gruppe verwalten', hidden: !group, onSelect: () => setManage(true) },
            { id: 'add', label: 'Personen hinzufügen', hidden: !group, onSelect: () => overlays.open({ type: 'newDm', channelId }) },
            { id: 'close', label: group ? 'Gruppe verlassen' : 'Unterhaltung schließen', danger: group, separatorBefore: true, onSelect: () => close() },
          ]}
        />
      )}
      {manage && <GroupDmSettings channelId={channelId} onClose={() => setManage(false)} />}
    </li>
  );
}
